import React, { useState } from 'react';
import { doc, collectionGroup, query, where, getDocs, limit, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import './ChildLinking.css';

function ChildLinking({ parentData, onChildLinked }) {
  const [studentId, setStudentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLinkChild = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (studentId.length === 0) {
        setError('Please enter a Student ID');
        return;
      }

      // Check if student exists in any class subcollection
      const studentsQuery = query(
        collectionGroup(db, 'students'),
        where('studentId', '==', studentId),
        limit(1)
      );
      const studentsSnapshot = await getDocs(studentsQuery);

      if (studentsSnapshot.empty) {
        setError('Student ID not found. Please verify with your child\'s teacher.');
        return;
      }

      const studentData = studentsSnapshot.docs[0].data();

      // Check if already linked to current parent
      if (parentData.linkedChildrenIds && parentData.linkedChildrenIds.includes(studentId)) {
        setError('This child is already linked to your account');
        return;
      }

      // Check if student ID is already linked to any parent
      const parentsQuery = query(
        collectionGroup(db, 'parents'),
        where('linkedChildrenIds', 'array-contains', studentId),
        limit(1)
      );
      const parentsSnapshot = await getDocs(parentsQuery);

      if (!parentsSnapshot.empty) {
        setError('This Student ID is already linked to a parent account. Please contact the school if you need to share access.');
        return;
      }

      // Link the child to parent
      const parentRef = doc(db, 'parents', parentData.id);
      await updateDoc(parentRef, {
        linkedChildrenIds: arrayUnion(studentId)
      });

      onChildLinked(studentData);
      setStudentId('');
    } catch (err) {
      setError('Failed to link child. Please try again.');
      console.error('Error linking child:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentIdChange = (e) => {
    const value = e.target.value.toUpperCase(); // Convert to uppercase
    setStudentId(value);
  };

  return (
    <div className="child-linking-container">
      <div className="child-linking-card">
        <h2>Add Child</h2>
        <p className="subtitle">Link your child using their Student ID</p>
        
        <form onSubmit={handleLinkChild}>
          <div className="form-section">
            <h3>Student Information</h3>
            
            <div className="form-group">
              <label>Enter Student ID</label>
              <input
                type="text"
                value={studentId}
                onChange={handleStudentIdChange}
                placeholder="SDFERT-2026"
                required
              />
              <small>Enter the Student ID provided by the school</small>
            </div>
          </div>

          <div className="info-section">
            <h3>Important Information</h3>
            <div className="info-item">
              <span className="info-icon green">✓</span>
              <span>Verify the Student ID with your child's teacher</span>
            </div>
            <div className="info-item">
              <span className="info-icon blue">ℹ</span>
              <span>You can link multiple children to your account</span>
            </div>
            <div className="info-item">
              <span className="info-icon orange">🔒</span>
              <span>Your child's data is secure and private</span>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            disabled={studentId.length === 0 || loading}
            className="link-button"
          >
            {loading ? (
              <>
                <div className="spinner"></div>
                Linking...
              </>
            ) : (
              'Link Child'
            )}
          </button>

          <button type="button" className="cancel-button" onClick={() => setStudentId('')}>
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChildLinking;
